import React, { useState, useEffect } from "react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useLanguage } from '../contexts/LanguageContext';
import { languages } from '../languages';
import { mcDePageClass } from '../utils/branding';
import { Typography } from "@material-tailwind/react";

const KG_TO_LB = 2.2046226218487757;
const CM_TO_IN = 1 / 2.54; // 1 cm = 1/2.54 in, using division keeps exactness

const WEIGHT_LIMITS = {
  kg: { min: 40, max: 175 },
  lb: { min: 88.185, max: 385.809 },
};

const HEIGHT_LIMITS = {
  cm: { min: 140, max: 200 },
  in: { min: 55.118, max: 78.74 },
};

function kgToLb(kg) {
  return kg * KG_TO_LB;
}
function lbToKg(lb) {
  return lb / KG_TO_LB;
}
function cmToIn(cm) {
  return cm * CM_TO_IN;
}
function inToCm(inn) {
  return inn / CM_TO_IN; // equals inn * 2.54
}

export default function PersonalizedGoalsForm() {
  const { language } = useLanguage();

  let translationLanguage = language;
  if (language === 'befr' || language === 'be-fr') translationLanguage = 'french';
  if (language === 'benl' || language === 'be-nl') translationLanguage = 'nederlands';
  if (language === 'bh-ar' || language === 'bahrain-arabic') translationLanguage = 'arabic';
  if (language === 'bh-en' || language === 'bahrain-english') translationLanguage = 'english';
  if (language === 'uae-ar' || language === 'uae-arabic') translationLanguage = 'arabic';
  if (language === 'uae-en' || language === 'uae-english') translationLanguage = 'english';
  if (language === 'sa-ar' || language === 'sa-arabic') translationLanguage = 'arabic';
  if (language === 'sa-en' || language === 'sa-english') translationLanguage = 'english';
  if (language === 'kw-ar' || language === 'kw-arabic') translationLanguage = 'arabic';
  if (language === 'kw-en' || language === 'kw-english') translationLanguage = 'english';
  if (language === 'qa-ar' || language === 'qa-arabic') translationLanguage = 'arabic';
  if (language === 'qa-en' || language === 'qa-english') translationLanguage = 'english';
  if (language === 'om-ar' || language === 'om-arabic') translationLanguage = 'arabic';
  if (language === 'om-en' || language === 'om-english') translationLanguage = 'english';
  if (language === 'iq-ar' || language === 'iq-arabic') translationLanguage = 'arabic';
  if (language === 'iq-en' || language === 'iq-english') translationLanguage = 'english';
  if (language === 'jo-ar' || language === 'jo-arabic') translationLanguage = 'arabic';
  if (language === 'jo-en' || language === 'jo-english') translationLanguage = 'english';
  if (language === 'lb-ar' || language === 'lb-arabic') translationLanguage = 'arabic';
  if (language === 'lb-en' || language === 'lb-english') translationLanguage = 'english';
  if (language === 'sy-ar' || language === 'sy-arabic') translationLanguage = 'arabic';
  if (language === 'sy-en' || language === 'sy-english') translationLanguage = 'english';
  if (language === 'ps-ar' || language === 'ps-arabic') translationLanguage = 'arabic';
  if (language === 'ps-en' || language === 'ps-english') translationLanguage = 'english';
  if (language === 'ye-ar' || language === 'ye-arabic') translationLanguage = 'arabic';
  if (language === 'ye-en' || language === 'ye-english') translationLanguage = 'english';
  if (language === 'eg-ar' || language === 'eg-arabic') translationLanguage = 'arabic';
  if (language === 'eg-en' || language === 'eg-english') translationLanguage = 'english';
  if (language === 'pl-pl') translationLanguage = 'polish';
    if (language === 'cyprus' || language === 'cy' || language === 'el-cy') translationLanguage = 'greek';

  const t = languages[translationLanguage] || languages['english'];

  // Keep displayed values in the unit the user selected.
  const [form, setForm] = useState({
    sex: "male",
    age: 30,
    // store value according to unit below
    weight: 70, // default in kg
    weightUnit: "kg",
    height: 175, // default in cm
    heightUnit: "cm",
    activity: "moderate",
    goal: "maintain",
    dietaryPref: "",
  });

  const [errors, setErrors] = useState({ weight: null, height: null });
  const [result, setResult] = useState(null);

  // Validation helpers
  function validateWeight(value, unit) {
    if (value === "" || value == null || Number.isNaN(Number(value))) {
      return t.pleaseEnterAWeight;
    }
    const v = Number(value);
    const { min, max } = WEIGHT_LIMITS[unit];
    if (v < min || v > max) {
      return t.validateWeight +` ${min} ` + t.and +` ${max} ${unit}`;
    }
    return null;
  }

  function validateHeight(value, unit) {
    if (value === "" || value == null || Number.isNaN(Number(value))) {
      return t.pleaseEnterAHeight;
    }
    const v = Number(value);
    const { min, max } = HEIGHT_LIMITS[unit];
    if (v < min || v > max) {
      return t.validateHeight+` ${min} `+ t.and +` ${max} ${unit}`;
    }
    return null;
  }

  // Run validation whenever weight/height or their units change
  useEffect(() => {
    setErrors((prev) => ({
      ...prev,
      weight: validateWeight(form.weight, form.weightUnit),
    }));
  }, [form.weight, form.weightUnit]);

  useEffect(() => {
    setErrors((prev) => ({
      ...prev,
      height: validateHeight(form.height, form.heightUnit),
    }));
  }, [form.height, form.heightUnit]);

  // Convert value when user switches units (preserve numeric value in new unit).
  function handleWeightUnitChange(newUnit) {
    if (newUnit === form.weightUnit) return;
    let newWeight = Number(form.weight) || 0;
    if (form.weightUnit === "kg" && newUnit === "lb") {
      newWeight = kgToLb(newWeight);
    } else if (form.weightUnit === "lb" && newUnit === "kg") {
      newWeight = lbToKg(newWeight);
    }
    // Keep 3 decimals to be user-friendly
    setForm((f) => ({ ...f, weightUnit: newUnit, weight: Number(newWeight.toFixed(3)) }));
  }

  function handleHeightUnitChange(newUnit) {
    if (newUnit === form.heightUnit) return;
    let newHeight = Number(form.height) || 0;
    if (form.heightUnit === "cm" && newUnit === "in") {
      newHeight = cmToIn(newHeight);
    } else if (form.heightUnit === "in" && newUnit === "cm") {
      newHeight = inToCm(newHeight);
    }
    setForm((f) => ({ ...f, heightUnit: newUnit, height: Number(newHeight.toFixed(3)) }));
  }

  // Build payload with kg & cm (server side expects base units)
  function buildPayload() {
    let weightKg = Number(form.weight);
    if (form.weightUnit === "lb") weightKg = lbToKg(weightKg);

    let heightCm = Number(form.height);
    if (form.heightUnit === "in") heightCm = inToCm(heightCm);

    return {
      sex: form.sex,
      age: Number(form.age),
      weightKg: Number(weightKg.toFixed(3)),
      heightCm: Number(heightCm.toFixed(2)),
      activity: form.activity,
      goal: form.goal,
      dietaryPref: form.dietaryPref,
    };
  }

  async function submit(e) {
    e.preventDefault();
    const weightError = validateWeight(form.weight, form.weightUnit);
    const heightError = validateHeight(form.height, form.heightUnit);
    setErrors({ weight: weightError, height: heightError });
    if (weightError || heightError) return;

    const payload = buildPayload();
    // send to server (example)
    const res = await fetch("/api/nutritionGoal", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const json = await res.json();
    setResult(json.goals);
  }

/* Responsive UI-only replacement: paste this in place of your current return(...) */
return (
  <div className={`w-full px-4 py-6 sm:px-6 sm:py-6 max-w-xl mx-auto bg-white rounded-lg shadow-md mb-6 ${mcDePageClass(language)}`}>
    <Typography variant='h4' className="mb-4">{t.nutritionGoals}</Typography>
    <form onSubmit={submit} className="space-y-6" aria-describedby="disclaimer">
      {/* top row: stacks on mobile, two columns on sm+ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t.sex}</label>
          <select
            value={form.sex}
            onChange={(e) => setForm({ ...form, sex: e.target.value })}
            className="block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option value="male">{t.male}</option>
            <option value="female">{t.female}</option>
            <option value="other">{t.other}</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t.age}</label>
          <input
            type="number"
            min={10}
            max={120}
            value={form.age}
            onChange={(e) => setForm({ ...form, age: Number(e.target.value) })}
            className="block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            aria-label="Age"
          />
        </div>
      </div>

      {/* weight + height: stacked on mobile, side-by-side on sm+ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t.weight}</label>

          <div className="flex items-center gap-2">
            <input
              type="number"
              step="0.001"
              inputMode="decimal"
              value={form.weight}
              onChange={(e) => setForm({ ...form, weight: e.target.value })}
              aria-invalid={!!errors.weight}
              className={`flex-1 rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${
                errors.weight ? 'border-red-500' : 'border-gray-200'
              }`}
            />
            <select
              value={form.weightUnit}
              onChange={(e) => handleWeightUnitChange(e.target.value)}
              className="w-28 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-green-500"
              aria-label="Weight unit"
            >
              <option value="kg">kg</option>
              <option value="lb">lb</option>
            </select>
          </div>

          {errors.weight ? (
            <p className="mt-1 text-sm text-red-600" role="alert">
              {errors.weight}
            </p>
          ) : (
            <p className="mt-1 text-sm text-gray-500">
              {t.allowed}: {WEIGHT_LIMITS[form.weightUnit].min}–{WEIGHT_LIMITS[form.weightUnit].max}{' '}
              {form.weightUnit}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t.height}</label>

          <div className="flex items-center gap-2">
            <input
              type="number"
              step="0.001"
              inputMode="decimal"
              value={form.height}
              onChange={(e) => setForm({ ...form, height: e.target.value })}
              aria-invalid={!!errors.height}
              className={`flex-1 rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${
                errors.height ? 'border-red-500' : 'border-gray-200'
              }`}
            />
            <select
              value={form.heightUnit}
              onChange={(e) => handleHeightUnitChange(e.target.value)}
              className="w-28 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-green-500"
              aria-label="Height unit"
            >
              <option value="cm">cm</option>
              <option value="in">in</option>
            </select>
          </div>

          {errors.height ? (
            <p className="mt-1 text-sm text-red-600" role="alert">
              {errors.height}
            </p>
          ) : (
            <p className="mt-1 text-sm text-gray-500">
              {t.allowed}: {HEIGHT_LIMITS[form.heightUnit].min}–{HEIGHT_LIMITS[form.heightUnit].max}{' '}
              {form.heightUnit}
            </p>
          )}
        </div>

      </div>

      {/* activity + goal */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t.activity}</label>
          <select
            value={form.activity}
            onChange={(e) => setForm({ ...form, activity: e.target.value })}
            className="block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option value="sedentary">{t.sedentary}</option>
            <option value="light">{t.light}</option>
            <option value="moderate">{t.moderate}</option>
            <option value="very">{t.veryActive}</option>
            <option value="extra">{t.extraActive}</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t.goal}</label>
          <select
            value={form.goal}
            onChange={(e) => setForm({ ...form, goal: e.target.value })}
            className="block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option value="lose">{t.lose}</option>
            <option value="maintain">{t.maintain}</option>
            <option value="gain">{t.gain}</option>
          </select>
        </div>
      </div>

      <div>
        <button
          type="submit"
          disabled={!!errors.weight || !!errors.height}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white font-medium rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
        >
          {t.computeGoals}
        </button>
      </div>
    </form>

    {result && (
      <div className="mt-6 bg-gray-50 border border-gray-100 rounded-md p-4 shadow-sm" aria-live="polite">
        <h3 className="text-sm font-semibold text-gray-700">{t.dailyTargets}</h3>
        <div className="mt-3 flex flex-col sm:flex-row items-baseline justify-between gap-4">
          <div>
            <p className="text-xs text-gray-500">{t.calories}</p>
            <p className="text-2xl font-bold text-gray-900">{result.calorieTarget} kcal</p>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center sm:text-right sm:grid-cols-1">
            <div>
              <p className="text-xs text-gray-500">{t.protein}</p>
              <p className="text-sm font-medium text-gray-800">{result.proteinG} g</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">{t.fat}</p>
              <p className="text-sm font-medium text-gray-800">{result.fatG} g</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">{t.carbs}</p>
              <p className="text-sm font-medium text-gray-800">{result.carbsG} g</p>
            </div>
          </div>
        </div>
      </div>
    )}

    <div
      id="disclaimer"
      role="note"
      aria-live="polite"
      className="mt-6 flex gap-3 items-start text-sm text-gray-600 border-t pt-4"
    >
      <InformationCircleIcon className="h-5 w-5 text-green-500 mt-0.5" aria-hidden="true" />
      <div>
        <strong className="block text-gray-800">{t.safetyAndDisclaimer}</strong>
        <p className="mt-1">
          {t.safetyAndDisclaimerText}
        </p>
      </div>
    </div>
  </div>
);


}
